import React from 'react';

import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { SearchBox } from '@bb-ui/react-library/dist/components/SearchBox';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';

import { useTranslation } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';
import {
    ADMIN_ROLE_NAME,
    AUTHOR_ROLE_NAME,
    DEVELOPER_ROLE_NAME,
    VIEWER_ROLE_NAME,
    VISIBLE_ROLES,
} from '../roles';
import { useStyles } from './UserList.styles';
import { mapToMenuItems } from '../../../../utilities/selectInput';
import { useIdentityProviderContext } from '../../../../contexts/IdentityProviderContext';

export const UserListTableFilters = (props: {
    setPage: React.Dispatch<React.SetStateAction<number>>;
    setNameFilter: React.Dispatch<React.SetStateAction<string>>;
    setRoleFilter: React.Dispatch<
        React.SetStateAction<undefined | (typeof VISIBLE_ROLES)[number]>
    >;
    setIdpIdFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    nameFilter: string | undefined;
    roleFilter: (typeof VISIBLE_ROLES)[number] | undefined;
    idpIdFilter: string | undefined;
    userCount: number;
}) => {
    const {
        setPage,
        setNameFilter,
        setRoleFilter,
        setIdpIdFilter,
        nameFilter,
        roleFilter,
        idpIdFilter,
        userCount,
    } = props;
    const classes = useStyles();
    const { t } = useTranslation();

    const { idps } = useIdentityProviderContext();

    return (
        <>
            <Typography variant="h3" component="h2" gutterBottom>
                {`${t('settings.licenseManagement.allUsers')} (${userCount})`}
            </Typography>
            <div className={classes.searchContainerBox}>
                <div className={classes.searchContainer}>
                    <SearchBox
                        inputId="license-management-entry-search-input"
                        label={t('settings.licenseManagement.search')}
                        InputProps={{
                            inputProps: {
                                'data-testid':
                                    'license-management-entry-search-input',
                            },
                        }}
                        onChange={newValue => {
                            setNameFilter(newValue);
                            setPage(1);
                        }}
                        onClear={() => {
                            setNameFilter('');
                            setPage(1);
                        }}
                        value={nameFilter}
                        className={classes.searchBox}
                    />
                    <TextField
                        select
                        id="license-management-entry-filter-by-role-input"
                        label={t(
                            'settings.licenseManagement.filterByRole.title',
                        )}
                        inputProps={{ 'data-testid': 'filter-by-role' }}
                        floatingLabel={true}
                        value={roleFilter ?? 'ALL'}
                        onChange={t => {
                            setRoleFilter(
                                t.target.value === 'ALL'
                                    ? undefined
                                    : (t.target.value as any),
                            );
                            setPage(1);
                        }}
                        className={classes.filterByTextField}
                    >
                        {mapToMenuItems([
                            {
                                key: 'ALL',
                                name: t(
                                    'settings.licenseManagement.filterByRole.allRoles',
                                ),
                            },
                            {
                                key: ADMIN_ROLE_NAME,
                                name: t(
                                    `settings.illuminateRoles.${ADMIN_ROLE_NAME}.displayName`,
                                ),
                            },
                            {
                                key: AUTHOR_ROLE_NAME,
                                name: t(
                                    `settings.illuminateRoles.${AUTHOR_ROLE_NAME}.displayName`,
                                ),
                            },
                            {
                                key: VIEWER_ROLE_NAME,
                                name: t(
                                    `settings.illuminateRoles.${VIEWER_ROLE_NAME}.displayName`,
                                ),
                            },
                            {
                                key: DEVELOPER_ROLE_NAME,
                                name: t(
                                    `settings.illuminateRoles.${DEVELOPER_ROLE_NAME}.displayName`,
                                ),
                            },
                        ])}
                    </TextField>
                    <TextField
                        select
                        id="license-management-entry-filter-by-idp-input"
                        label={t(
                            'settings.licenseManagement.filterByIdentityProvider.title',
                        )}
                        inputProps={{ 'data-testid': 'filter-by-status' }}
                        floatingLabel={true}
                        value={idpIdFilter ?? 'ALL'}
                        onChange={t => {
                            setIdpIdFilter(
                                t.target.value === 'ALL'
                                    ? undefined
                                    : t.target.value,
                            );

                            setPage(1);
                        }}
                        className={classes.filterByTextField}
                    >
                        {mapToMenuItems([
                            {
                                key: 'ALL',
                                name: t(
                                    'settings.licenseManagement.filterByStatus.all',
                                ),
                            },
                            ...(idps || []).map(idp => ({
                                key: idp.id,
                                name: idp.displayName,
                            })),
                        ])}
                    </TextField>
                </div>

                <RouteLink
                    to="/settings/users/create"
                    data-testid="create-user-button"
                    className={classes.createUserButton}
                >
                    <Typography component="span" variant="subtitle2">
                        {t('settings.licenseManagement.createUserButton')}
                    </Typography>
                </RouteLink>
            </div>
        </>
    );
};
